// src/components/foods/Foods.jsx
import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import "./foods.css";
import FoodStock from "./FoodStock";
import FoodDealers from "./FoodDealers";
import { fetchBillDateRanges } from "../../services/sanklan";
import { fetchKhNames, fetchKhSaleRows, saveKhSale } from "../../services/foods";
import { useCustomerRepo } from "../../repos/AppRepoProvider";
import FoodDealersPayment from "./FoodDealersPayment";

/* ---------------- utils (same style as CollectionAdjustment) ---------------- */
function pad2(n) {
  const x = String(n);
  return x.length === 1 ? `0${x}` : x;
}

function todayIsoLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function todayDmy() {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function isoToDmy(iso) {
  const s = String(iso || "").trim().slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function dmyToIso(dmy) {
  const s = String(dmy || "").trim();
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${pad2(m[2])}-${pad2(m[1])}`;
}

// ---------------- UTC-safe date helpers (copied from CollectionAdjustment) ----------------
function isoToUtcMs(iso) {
  const s = String(iso || "").slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return NaN;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
function utcMsToIso(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}
function isoDateUTC(iso) {
  return new Date(String(iso).slice(0, 10) + "T00:00:00Z");
}
function addDaysIso(iso, days) {
  const d = isoDateUTC(iso);
  d.setUTCDate(d.getUTCDate() + Number(days || 0));
  return d.toISOString().slice(0, 10);
}
function ymLengthOfMonth(y, m1to12) {
  return new Date(Date.UTC(y, m1to12, 0)).getUTCDate();
}
function labelRange(date1, date2) {
  try {
    const ms1 = isoToUtcMs(date1);
    const ms2 = isoToUtcMs(date2);
    const d1 = new Date(ms1);
    const d2 = new Date(ms2);
    const fmt = (d) =>
      `${d.getUTCDate()} ${d.toLocaleString("en-US", { month: "short", timeZone: "UTC" })} ${d.getUTCFullYear()}`;
    return `${fmt(d1)} - ${fmt(d2)}`;
  } catch {
    return `${date1} - ${date2}`;
  }
}

// ---------------- period inference (same as CollectionAdjustment) ----------------
const PeriodMode = {
  DECADAL_1_10_11_20_21_ME: "DECADAL_1_10_11_20_21_ME",
  HALF_1_15_16_ME: "HALF_1_15_16_ME",
  MONTH_1_ME: "MONTH_1_ME",
  QUIN_5DAY: "QUIN_5DAY",
  DECADAL_SHIFTED_6_16_26: "DECADAL_SHIFTED_6_16_26",
};

function normPeriodString(raw) {
  return String(raw || "")
    .replace(/\s+/g, "")
    .replace(/०/g, "0")
    .replace(/१/g, "1")
    .replace(/२/g, "2")
    .replace(/३/g, "3")
    .replace(/४/g, "4")
    .replace(/५/g, "5")
    .replace(/६/g, "6")
    .replace(/७/g, "7")
    .replace(/८/g, "8")
    .replace(/९/g, "9");
}

function parsePeriod(raw) {
  const s = normPeriodString(raw).toLowerCase();

  const reDecadal = /^0?1-0?10,0?11-0?20,0?21-.*$/i;
  const reHalf = /^0?1-0?15,0?16-.*$/i;
  const reMonth = /^0?1-.*$/i;
  const reQuin = /^0?1-0?5,0?6-0?10,0?11-0?15,0?16-0?20,0?21-0?25,0?26-.*$/i;
  const reShifted = /^0?6-0?15,0?16-0?25,0?26-0?5$/i;

  if (reDecadal.test(s)) return { mode: PeriodMode.DECADAL_1_10_11_20_21_ME };
  if (reHalf.test(s)) return { mode: PeriodMode.HALF_1_15_16_ME };
  if (reQuin.test(s)) return { mode: PeriodMode.QUIN_5DAY };
  if (reShifted.test(s)) return { mode: PeriodMode.DECADAL_SHIFTED_6_16_26 };
  if (reMonth.test(s)) return { mode: PeriodMode.MONTH_1_ME };

  return { mode: PeriodMode.MONTH_1_ME };
}

function dayOfMonthUTC(iso) {
  return isoDateUTC(iso).getUTCDate();
}
function yearUTC(iso) {
  return isoDateUTC(iso).getUTCFullYear();
}
function monthUTC1to12(iso) {
  return isoDateUTC(iso).getUTCMonth() + 1;
}
function monthEndIsoUTC(iso) {
  const y = yearUTC(iso);
  const m = monthUTC1to12(iso);
  const me = ymLengthOfMonth(y, m);
  return `${y}-${pad2(m)}-${pad2(me)}`;
}
function isMonthEndUTC(iso) {
  return String(iso).slice(0, 10) === monthEndIsoUTC(iso);
}

function inferModeFromLastRange(lastStartIso, lastEndIso, periodSetting) {
  const sDay = dayOfMonthUTC(lastStartIso);
  const eDay = dayOfMonthUTC(lastEndIso);

  if (sDay === 1 && eDay === 10) return { mode: PeriodMode.DECADAL_1_10_11_20_21_ME };
  if (sDay === 1 && eDay === 15) return { mode: PeriodMode.HALF_1_15_16_ME };
  if (sDay === 1 && eDay === 5) return { mode: PeriodMode.QUIN_5DAY };

  if ((sDay === 6 && eDay === 15) || (sDay === 16 && eDay === 25) || (sDay === 26 && eDay === 5)) {
    return { mode: PeriodMode.DECADAL_SHIFTED_6_16_26 };
  }

  if (sDay === 1 && isMonthEndUTC(lastEndIso)) return { mode: PeriodMode.MONTH_1_ME };

  return parsePeriod(periodSetting);
}

function nextBucketEndFor(startIso, spec) {
  const d = isoDateUTC(startIso);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + 1; // 1..12
  const dom = d.getUTCDate();
  const me = ymLengthOfMonth(y, m);

  const isoOf = (yy, mm, dd) => `${yy}-${pad2(mm)}-${pad2(dd)}`;

  switch (spec.mode) {
    case PeriodMode.DECADAL_1_10_11_20_21_ME:
      if (dom <= 10) return isoOf(y, m, 10);
      if (dom <= 20) return isoOf(y, m, 20);
      return isoOf(y, m, me);

    case PeriodMode.HALF_1_15_16_ME:
      if (dom <= 15) return isoOf(y, m, 15);
      return isoOf(y, m, me);

    case PeriodMode.QUIN_5DAY:
      if (dom <= 5) return isoOf(y, m, 5);
      if (dom <= 10) return isoOf(y, m, 10);
      if (dom <= 15) return isoOf(y, m, 15);
      if (dom <= 20) return isoOf(y, m, 20);
      if (dom <= 25) return isoOf(y, m, 25);
      return isoOf(y, m, me);

    case PeriodMode.DECADAL_SHIFTED_6_16_26:
      if (dom >= 6 && dom <= 15) return isoOf(y, m, 15);
      if (dom >= 16 && dom <= 25) return isoOf(y, m, 25);
      if (dom >= 26) {
        const nm = new Date(Date.UTC(y, m - 1, 1));
        nm.setUTCMonth(nm.getUTCMonth() + 1);
        return `${nm.getUTCFullYear()}-${pad2(nm.getUTCMonth() + 1)}-05`;
      }
      return isoOf(y, m, 5);

    case PeriodMode.MONTH_1_ME:
    default:
      return isoOf(y, m, me);
  }
}

function buildExtendedRanges(existingRanges, periodSetting) {
  if (!Array.isArray(existingRanges) || existingRanges.length === 0) return [];

  let last = null;
  for (const r of existingRanges) {
    const d1 = String(r.date1 || "").trim();
    const d2 = String(r.date2 || "").trim();
    if (!d1 || !d2) continue;
    if (!last || d2 > last.date2) last = { date1: d1, date2: d2 };
  }
  if (!last?.date2) return existingRanges;

  const spec = inferModeFromLastRange(last.date1, last.date2, periodSetting);

  const todayLocal = todayIsoLocal();
  const todayUtcIso = utcMsToIso(isoToUtcMs(todayLocal));
  const currentPeriodEnd = nextBucketEndFor(todayUtcIso, spec);

  const out = [...existingRanges];
  const seen = new Set(out.map((r) => `${r.date1}|${r.date2}`));

  let pointer = addDaysIso(last.date2, 1);
  let guard = 0;

  while (pointer <= currentPeriodEnd && guard < 500) {
    guard++;
    let end = nextBucketEndFor(pointer, spec);
    if (end > currentPeriodEnd) end = currentPeriodEnd;

    const k = `${pointer}|${end}`;
    if (!seen.has(k)) {
      out.push({ date1: pointer, date2: end, date_range: "" });
      seen.add(k);
    }

    if (end >= currentPeriodEnd) break;
    pointer = addDaysIso(end, 1);
  }

  out.sort((A, B) => {
    const c = String(B.date1 || "").localeCompare(String(A.date1 || ""));
    return c !== 0 ? c : String(B.date2 || "").localeCompare(String(A.date2 || ""));
  });

  return out;
}

/* ---------------- small helpers ---------------- */
function safeParseArray(v) {
  try {
    const s = typeof v === "string" ? v.trim() : v;
    const out = typeof s === "string" ? JSON.parse(s) : s;
    return Array.isArray(out) ? out : [];
  } catch {
    return [];
  }
}

function fmt2(x) {
  const n = Number(x || 0);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
}

function numStr(v) {
  const s = String(v ?? "").trim();
  if (!s) return "0";
  const n = Number(s);
  return Number.isFinite(n) ? String(n) : "0";
}

/* ---------------- Modal with Portal ---------------- */
function PortalModal({ children }) {
  return ReactDOM.createPortal(children, document.body);
}

/* ---------------- Food Picker Modal ---------------- */
function FoodPickerModal({ open, onClose, onPick, mainuser }) {
  const [loading, setLoading] = useState(false);
  const [screen, setScreen] = useState("sale"); // "sale" | "stock" | "dealers"
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  const purdate = localStorage.getItem("purdate") || ""; // same as Android Globaliccha.purdate

  useEffect(() => {
    if (!open) return;

    (async () => {
      setLoading(true);
      try {
        const data = await fetchKhNames({ mainuser, purdate });
        const arr = safeParseArray(data);

        // keep only usable rows (same rule as Android: stock not "-" and not "0")
        const mapped = arr
          .map((o, idx) => ({
            code: o?.code ?? String(idx + 1),
            khname: String(o?.khname ?? "").trim(),
            current_stock: String(o?.current_stock ?? "").trim(),
            rate: String(o?.rate ?? "").trim(),
            purrate: String(o?.rate ?? "").trim(),
            srate: String(o?.srate ?? "").trim(),
            purdate: String(o?.date ?? "").trim(),
          }))
          .filter((r) => r.khname && r.current_stock !== "-" && r.current_stock !== "0");

        setRows(mapped);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, mainuser, purdate]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) => String(r.code).toLowerCase().includes(s) || String(r.khname).toLowerCase().includes(s)
    );
  }, [rows, q]);

  if (!open) return null;

  return (
    <PortalModal>
      <div className="fdBackdrop" onMouseDown={onClose}>
        <div className="fdModal fdPickModal" onMouseDown={(e) => e.stopPropagation()}>
          <div className="fdModalHead">
            <div className="fdPickHeadTitle">Foods</div>
            <button type="button" className="fdCloseBtn" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>

          <div className="fdPickTop">
            <div className="fdPickCaption">Foods</div>
            <div className="foodsSearchBox">
              <span className="foodsSearchLabel">Search</span>
              <input
                className="foodsInput"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search"
              />
            </div>
          </div>

          <div className="foodsTableWrap">
            <table className="foodsTable" style={{ minWidth: 820 }}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th className="foodsNum">Quantity</th>
                  <th className="foodsNum">Sale Rate</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="foodsEmpty">
                      Loading...
                    </td>
                  </tr>
                ) : visible.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="foodsEmpty">
                      No foods
                    </td>
                  </tr>
                ) : (
                  visible.map((r) => (
                    <tr
                      key={`${r.code}-${r.khname}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => onPick(r)}
                      title="Click to select"
                    >
                      <td>{r.code}</td>
                      <td>
                        <b>{r.khname}</b>
                      </td>
                      <td className="foodsNum">{r.current_stock}</td>
                      <td className="foodsNum">{fmt2(r.rate)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="foodsFooter">
            <div style={{ color: "#666" }}>Showing 1 to {visible.length} of {visible.length} entries</div>
            <div />
          </div>
        </div>
      </div>
    </PortalModal>
  );
}

/* ---------------- Add/Edit Food Sale Modal ---------------- */
function AddFoodSaleModal({ open, onClose, onSaved, mainuser, fromIso, toIso, editRow }) {
  const customers = useCustomerRepo();

  const [kno, setKno] = useState("");
  const [cname, setCname] = useState("");
  const [mobno, setMobno] = useState("");

  const [dateDmy, setDateDmy] = useState(todayDmy());

  const [khname, setKhname] = useState("");
  const [purdate, setPurdate] = useState("");
  const [purrate, setPurrate] = useState("");
  const [srate, setSrate] = useState("");

  const [rate, setRate] = useState("");
  const [quin, setQuin] = useState("1");
  const [amt, setAmt] = useState("");

  const [receipt, setReceipt] = useState("");
  const [cashPay, setCashPay] = useState("No");
  const [saveContinue, setSaveContinue] = useState(false);

  const [saving, setSaving] = useState(false);
  const [foodPickerOpen, setFoodPickerOpen] = useState(false);

  // fill in edit
  useEffect(() => {
    if (!open) return;

    if (editRow) {
      setKno(String(editRow.kno ?? ""));
      setDateDmy(editRow.date ? isoToDmy(editRow.date) : todayDmy());
      setKhname(String(editRow.khname ?? ""));
      setRate(String(editRow.rate ?? ""));
      setQuin(String(editRow.quin ?? "1"));
      setAmt(String(editRow.amt ?? ""));
      setReceipt(String(editRow.remark ?? ""));
      setPurdate(String(editRow.purdate ?? ""));
      setPurrate(String(editRow.purrate ?? ""));
      setSrate(String(editRow.srate ?? ""));
      setCashPay(String(editRow.cashPay ?? "No"));
      setSaveContinue(false);
    } else {
      setKno("");
      setDateDmy(todayDmy());
      setKhname("");
      setPurdate("");
      setPurrate("");
      setSrate("");
      setRate("");
      setQuin("1");
      setAmt("");
      setReceipt("");
      setCashPay("No");
      setSaveContinue(false);
    }
  }, [open, editRow]);

  // cname/mob from repo
  useEffect(() => {
    const kn = String(kno || "").trim();
    if (!kn || !customers?.ready) {
      setCname("");
      setMobno("");
      return;
    }
    setCname(customers.getNameByKno?.(kn) || "");
    try {
      setMobno(customers.getMobByKno?.(kn) || "");
    } catch {
      setMobno("");
    }
  }, [kno, customers]);

  // calc amount (rate * quin)
  useEffect(() => {
    const r = Number(rate || 0);
    const q = Number(quin || 0);
    if (Number.isFinite(r) && Number.isFinite(q) && r > 0 && q > 0) {
      setAmt((r * q).toFixed(2));
    } else {
      setAmt("");
    }
  }, [rate, quin]);

  function validate() {
    if (!String(kno).trim()) return "Farmer (KNO) required";
    if (!String(dateDmy).trim()) return "Date required";
    if (!String(khname).trim()) return "Food required";
    if (!(Number(rate) > 0)) return "Rate required";
    if (!(Number(quin) > 0)) return "Quantity required";
    if (!(Number(amt) > 0)) return "Amount required";
    return "";
  }

  async function onSave() {
    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    const mobileuser = localStorage.getItem("mobileuser") || String(mainuser || "");
    const usertype =
      localStorage.getItem("mobileusertype") || localStorage.getItem("usertype") || "";

    setSaving(true);
    try {
      const payload = {
        mainuser: String(mainuser || ""),
        date: String(dateDmy || ""), // dd/MM/yyyy (same as Android)
        date1: isoToDmy(fromIso),
        date2: isoToDmy(toIso),
        kno: String(kno || ""),
        khname: String(khname || ""),
        purdate: String(purdate || ""),
        quin: numStr(quin),
        rate: numStr(rate),
        amt: numStr(amt),
        sredit: editRow?.sr ? String(editRow.sr) : "-",
        mobileuser: String(mobileuser || ""),
        flag: editRow ? "edit" : "new",
        remark: String(receipt || ""),
        usertype: String(usertype || ""),
        cashpay: String(cashPay || ""),
      };

      const resText = await saveKhSale(payload);
      await onSaved?.();

      if (saveContinue) {
        setKhname("");
        setPurdate("");
        setPurrate("");
        setSrate("");
        setRate("");
        setQuin("1");
        setAmt("");
        setReceipt("");
        setCashPay("No");
      } else {
        onClose();
      }

      void resText;
    } catch (e) {
      alert(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <>
      <PortalModal>
        <div className="fdBackdrop" onMouseDown={onClose}>
          <div className="fdModal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="fdModalHead">
              <div className="fdModalHeadTitle">{editRow ? "Edit Food Sale" : "Add Food Sale"}</div>
              <button type="button" className="fdCloseBtn" onClick={onClose} aria-label="Close">
                ×
              </button>
            </div>

            <div className="fdModalBody">
              <div className="fdFormGrid">
                <div className="fdLabel">
                  Farmer <span className="fdReq">*</span>
                </div>
                <div className="fdFieldRow">
                  <input className="fdInput" value={kno} onChange={(e) => setKno(e.target.value)} placeholder="KNO" />
                  <button type="button" className="fdIconBtn" title="Focus KNO" onClick={() => { }}>
                    🔍
                  </button>
                </div>

                <div />
                <div style={{ fontSize: 12, opacity: 0.85 }}>
                  {cname ? <b>{cname}</b> : "\u00A0"} {mobno ? ` • ${mobno}` : ""}
                </div>

                <div className="fdLabel">
                  Date <span className="fdReq">*</span>
                </div>
                <div>
                  <input className="fdInput" value={dateDmy} onChange={(e) => setDateDmy(e.target.value)} placeholder="dd/MM/yyyy" />
                </div>

                <div className="fdLabel">
                  Food <span className="fdReq">*</span>
                </div>
                <div className="fdFieldRow">
                  <input className="fdInput" value={khname} readOnly placeholder="Select food" />
                  <button type="button" className="fdIconBtn" title="Select food" onClick={() => setFoodPickerOpen(true)}>
                    🔍
                  </button>
                </div>

                <div className="fdLabel">
                  Rate <span className="fdReq">*</span>
                </div>
                <div>
                  <input className="fdInput" value={rate} onChange={(e) => setRate(e.target.value)} />
                </div>

                <div className="fdLabel">
                  Quantity <span className="fdReq">*</span>
                </div>
                <div>
                  <input className="fdInput" value={quin} onChange={(e) => setQuin(e.target.value)} />
                </div>

                <div className="fdLabel">
                  Amount <span className="fdReq">*</span>
                </div>
                <div>
                  <input className="fdInput" value={amt} readOnly />
                </div>

                <div className="fdLabel">Receipt No :</div>
                <div>
                  <input className="fdInput" value={receipt} onChange={(e) => setReceipt(e.target.value)} />
                </div>

                <div className="fdLabel">
                  Cash payment : <span className="fdReq">*</span>
                </div>
                <div className="fdRadioRow">
                  <label><input type="radio" checked={cashPay === "No"} onChange={() => setCashPay("No")} /> No</label>
                  <label><input type="radio" checked={cashPay === "Yes"} onChange={() => setCashPay("Yes")} /> Yes</label>
                </div>

                <div />
                <div className="fdCheckboxRow">
                  <label>
                    <input type="checkbox" checked={saveContinue} onChange={(e) => setSaveContinue(e.target.checked)} />{" "}
                    Save and continue
                  </label>
                </div>
              </div>

              <div className="fdActions">
                <button
                  type="button"
                  className="fdBtnGhost"
                  onClick={() => {
                    setKhname("");
                    setPurdate("");
                    setPurrate("");
                    setSrate("");
                    setRate("");
                    setQuin("1");
                    setAmt("");
                    setReceipt("");
                    setCashPay("No");
                  }}
                  disabled={saving}
                >
                  Reset
                </button>

                <button type="button" className="fdBtnPrimary" onClick={onSave} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </PortalModal>

      <FoodPickerModal
        open={foodPickerOpen}
        onClose={() => setFoodPickerOpen(false)}
        mainuser={mainuser}
        onPick={(item) => {
          setKhname(item.khname);
          setRate(String(item.rate || ""));
          setPurdate(item.purdate || "");
          setPurrate(item.purrate || "");
          setSrate(item.srate || "");
          setFoodPickerOpen(false);
        }}
      />
    </>
  );
}

/* ---------------- Main Foods page ---------------- */
/* ---------------- Main Foods page ---------------- */
export default function Foods({ mainuser }) {
  const periodSetting =
    localStorage.getItem("vikriperiodsettings") ||
    localStorage.getItem("paymentperiodsettings") ||
    "01-ME";

  // ✅ screen switch (NO early return!)
  const [screen, setScreen] = useState("sale"); // "sale" | "stock"

  const [rangeList, setRangeList] = useState([]);
  const [selRangeKey, setSelRangeKey] = useState("");
  const [fromIso, setFromIso] = useState("");
  const [toIso, setToIso] = useState("");

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  // load bill ranges once + extend
  useEffect(() => {
    // ✅ optional: don't run sale screen effects while on stock screen
    if (screen !== "sale") return;
    if (!mainuser) return;

    (async () => {
      try {
        const resp = await fetchBillDateRanges({
          mainuser,
          paymentperiodsettings: periodSetting,
        });

        let list = Array.isArray(resp?.rows) ? resp.rows : [];
        list = list
          .map((r) => ({
            date1: String(r?.date1 || "").trim(),
            date2: String(r?.date2 || "").trim(),
            date_range: "",
          }))
          .filter((r) => r.date1 && r.date2);

        list = buildExtendedRanges(list, periodSetting);
        list = list.map((r) => ({ ...r, date_range: labelRange(r.date1, r.date2) }));

        setRangeList(list);

        if (list.length) {
          const newest = list[0];
          const key = `${newest.date1}|${newest.date2}`;
          setSelRangeKey(key);
          setFromIso(newest.date1);
          setToIso(newest.date2);
        } else {
          setSelRangeKey("");
          setFromIso("");
          setToIso("");
        }
      } catch (e) {
        console.error("bill ranges error", e);
        setRangeList([]);
        setSelRangeKey("");
        setFromIso("");
        setToIso("");
      }
    })();
  }, [mainuser, periodSetting, screen]);

  async function loadSales() {
    // ✅ optional: don't load while on stock screen
    if (screen !== "sale") return;

    if (!mainuser || !fromIso || !toIso) return;

    const mobileuser = localStorage.getItem("mobileuser") || String(mainuser || "");
    const usertype =
      localStorage.getItem("mobileusertype") || localStorage.getItem("usertype") || "";

    setLoading(true);
    try {
      const data = await fetchKhSaleRows({
        mainuser,
        mobileuser,
        date1: isoToDmy(fromIso),
        date2: isoToDmy(toIso),
        usertype,
      });

      const arr = safeParseArray(data);

      const mapped = arr.map((o, idx) => ({
        srno: idx + 1,
        sr: o?.sr ?? o?.sredit ?? "",
        pavti: o?.remark ?? o?.receipt ?? "-",
        code: o?.code ?? o?.kno ?? "-",
        name: o?.name ?? o?.cname ?? "-",
        date: o?.date ? String(o.date).slice(0, 10) : "",
        khname: o?.khname ?? "",
        quin: o?.quin ?? o?.quantity ?? "",
        rate: o?.rate ?? "",
        amt: o?.amt ?? "",
        kno: o?.kno ?? "",
        purdate: o?.purdate ?? "",
        purrate: o?.purrate ?? "",
        srate: o?.srate ?? "",
        remark: o?.remark ?? "",
      }));

      setRows(mapped);
    } finally {
      setLoading(false);
    }
  }

  // auto load when range changes
  useEffect(() => {
    if (screen !== "sale") return;
    loadSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromIso, toIso, mainuser, screen]);

  function onRangeChange(val) {
    setSelRangeKey(val);
    const [d1, d2] = String(val || "").split("|");
    setFromIso(d1 || "");
    setToIso(d2 || "");
  }

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      return (
        String(r.pavti || "").toLowerCase().includes(q) ||
        String(r.code || "").toLowerCase().includes(q) ||
        String(r.name || "").toLowerCase().includes(q) ||
        String(r.khname || "").toLowerCase().includes(q)
      );
    });
  }, [rows, search]);

  const totals = useMemo(() => {
    let q = 0;
    let amt = 0;
    let rate = 0;
    for (const r of visibleRows) {
      q += Number(r.quin || 0) || 0;
      amt += Number(r.amt || 0) || 0;
      rate += Number(r.rate || 0) || 0;
    }
    return { q, amt, rate };
  }, [visibleRows]);

  // ✅ ONLY switch UI in the return (NO conditional hooks)
  return screen === "stock" ? (
    <FoodStock mainuser={mainuser} onBack={() => setScreen("sale")} />
  ) : screen === "dealers" ? (
    <FoodDealers mainuser={mainuser} onBack={() => setScreen("sale")} />
  ) : screen === "dealers_payment" ? (
    <FoodDealersPayment mainuser={mainuser} onBack={() => setScreen("sale")} />
  ) 
  : (
    <div className="foodsPage">
      <div className="foodsTop">
        <div className="foodsTitle">
          <span style={{ fontSize: 18 }}>📖</span>
          <span>Foods</span>
        </div>

        <div className="foodsActions">
          <button
            className="foodsLinkBtn"
            onClick={() => {
              setEditRow(null);
              setAddOpen(true);
            }}
          >
            ＋ Add Food Sale
          </button>

          {/* ✅ OPEN FoodStock */}
          <button className="foodsLinkBtn" type="button" onClick={() => setScreen("stock")}>
            🛒 Food Stock
          </button>
          <button className="foodsLinkBtn" type="button" onClick={() => setScreen("dealers")}>
            👥 Food Dealers
          </button>
          <button className="foodsLinkBtn" type="button" onClick={() => setScreen("dealers_payment")}>
            ₱ Food Dealers Payment
          </button>
        </div>
      </div>

      <div className="foodsFilters">
        <div className="foodsFilter">
          <span className="foodsLabel">Date Range :</span>
          <select className="foodsSelect" value={selRangeKey} onChange={(e) => onRangeChange(e.target.value)}>
            {rangeList.map((r) => {
              const key = `${r.date1}|${r.date2}`;
              return (
                <option key={key} value={key}>
                  {r.date_range}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      <div className="foodsCard">
        <div className="foodsCardHead">
          <div className="foodsCaption">Foods</div>

          <div className="foodsSearchBox">
            <button className="foodsLinkBtn" type="button">
              🖨 Print
            </button>
            <span className="foodsSearchLabel">Search</span>
            <input
              className="foodsInput"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
            />
          </div>
        </div>

        <div className="foodsTableWrap">
          <table className="foodsTable">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input type="checkbox" />
                </th>
                <th>SrNo</th>
                <th>Pavti No</th>
                <th>Code</th>
                <th>Name</th>
                <th>Date</th>
                <th>Food</th>
                <th className="foodsNum">Quantity</th>
                <th className="foodsNum">Rate</th>
                <th className="foodsNum">Amount</th>
                <th style={{ width: 90 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="foodsEmpty">
                    Loading...
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="foodsEmpty">
                    No rows
                  </td>
                </tr>
              ) : (
                <>
                  {visibleRows.map((r) => (
                    <tr key={`${r.srno}-${r.khname}-${r.date}`}>
                      <td>
                        <input type="checkbox" />
                      </td>
                      <td>{r.srno}</td>
                      <td>{r.pavti || "-"}</td>
                      <td>{r.code || "-"}</td>
                      <td>
                        <b>{r.name || "-"}</b>
                      </td>
                      <td>{r.date ? labelRange(r.date, r.date).split(" - ")[0] : "-"}</td>
                      <td>
                        <b>{r.khname}</b>
                      </td>
                      <td className="foodsNum">{r.quin}</td>
                      <td className="foodsNum">{fmt2(r.rate)}</td>
                      <td className="foodsNum">{fmt2(r.amt)}</td>
                      <td>
                        <button
                          className="foodsLinkBtn"
                          type="button"
                          title="Edit"
                          onClick={() => {
                            setEditRow(r);
                            setAddOpen(true);
                          }}
                        >
                          ✎
                        </button>
                        <button className="foodsLinkBtn" type="button" title="Delete" style={{ color: "red" }}>
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))}

                  <tr className="foodsTotalRow">
                    <td>-</td>
                    <td>-</td>
                    <td>
                      <b>Total</b>
                    </td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td className="foodsNum">{fmt2(totals.q)}</td>
                    <td className="foodsNum">{fmt2(totals.rate)}</td>
                    <td className="foodsNum">{fmt2(totals.amt)}</td>
                    <td>-</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        <div className="foodsFooter">
          <div style={{ color: "#666" }}>
            Showing {visibleRows.length ? 1 : 0} to {visibleRows.length} of {visibleRows.length} entries
          </div>
          <div />
        </div>
      </div>

      <AddFoodSaleModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={loadSales}
        mainuser={mainuser}
        fromIso={fromIso}
        toIso={toIso}
        editRow={editRow}
      />
    </div>
  );
}
